import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import { DOMSerializer } from 'prosemirror-model';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { useEffect, useState } from 'react';
import {
  Bold, Italic, Strikethrough, Heading1, Heading2, Heading3, List, ListOrdered,
  Link as LinkIcon, Quote, Code, Code2, Minus, Image as ImageIcon, Undo, Redo,
  AlignLeft, AlignCenter, AlignRight, RemoveFormatting,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { MediaPicker } from '@/features/media/MediaPicker';
import { SmartPaste } from './smart-paste';
import { AIActionButton } from '@/features/ai/AIActionButton';
import type { AiActionKey, AiFormat } from '@/lib/ai';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  /** Hide the toolbar (only the editor area is shown). */
  hideToolbar?: boolean;
  /** Compact toolbar without H1 and a few extras. */
  compact?: boolean;
}

export function RichTextEditor({
  value, onChange, placeholder, minHeight = '160px', hideToolbar = false, compact = false,
}: RichTextEditorProps) {
  const [mediaOpen, setMediaOpen] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'underline' } }),
      Image.configure({ inline: false, allowBase64: false, HTMLAttributes: { class: 'rounded-md' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: placeholder ?? 'Empieza a escribir…' }),
      SmartPaste.configure({
        onError: (msg) => toast.error(msg),
      }),
    ],
    content: value || '',
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: cn(
          'rte-prose focus:outline-none',
          'mx-auto w-full max-w-[760px]',
          'px-6 py-10 sm:px-10 sm:py-14',
          'min-h-[var(--rte-h)]',
        ),
        style: `--rte-h:${minHeight}`,
      },
    },
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  function setLink() {
    const url = window.prompt('URL del enlace (vacío para quitar):', editor?.getAttributes('link').href ?? '');
    if (url === null) return;
    if (url === '') {
      editor?.chain().focus().unsetLink().run();
      return;
    }
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }

  function insertImage(src: string) {
    if (!src) return;
    editor?.chain().focus().setImage({ src }).run();
  }

  function readAiInput(): { input: string; hasSelection: boolean } {
    if (!editor) return { input: '', hasSelection: false };
    const { from, to, empty } = editor.state.selection;
    if (!empty) {
      const slice = editor.state.doc.slice(from, to);
      const serializer = DOMSerializer.fromSchema(editor.schema);
      const fragment = serializer.serializeFragment(slice.content);
      const container = document.createElement('div');
      container.appendChild(fragment);
      const html = container.innerHTML.trim() || editor.state.doc.textBetween(from, to, '\n');
      return { input: html, hasSelection: true };
    }
    return { input: editor.getHTML(), hasSelection: false };
  }

  function applyAiResult(text: string, meta: { action: AiActionKey; format: AiFormat }) {
    if (!editor) return;
    const { hasSelection } = readAiInput();
    const html = meta.format === 'html' ? text : escapeHtml(text).replace(/\n/g, '<br/>');
    const chain = editor.chain().focus();
    if (hasSelection) {
      chain.deleteSelection().insertContent(html).run();
    } else {
      chain.setContent(html, { emitUpdate: true }).run();
    }
    toast.success(`AI: ${meta.action} aplicado`);
  }

  return (
    <div className="rounded-md border border-neutral-200 bg-white">
      {!hideToolbar && (
        <Toolbar
          editor={editor}
          compact={compact}
          onLink={setLink}
          onImage={() => setMediaOpen(true)}
          aiButton={
            <AIActionButton
              preset="richText"
              getInput={() => readAiInput().input}
              onApply={applyAiResult}
              variant="ghost"
              size="sm"
              align="end"
              stream
            />
          }
        />
      )}
      <EditorContent editor={editor} />
      {mediaOpen && (
        <MediaPicker
          open={mediaOpen}
          onClose={() => setMediaOpen(false)}
          onSelect={(url) => {
            insertImage(url);
            setMediaOpen(false);
          }}
        />
      )}
    </div>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

function Toolbar({
  editor, compact, onLink, onImage, aiButton,
}: {
  editor: Editor;
  compact: boolean;
  onLink: () => void;
  onImage: () => void;
  aiButton?: React.ReactNode;
}) {
  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 border-b border-neutral-200 bg-white/95 p-1 backdrop-blur">
      <TbBtn title="Negrita (⌘B)" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="size-3.5" />
      </TbBtn>
      <TbBtn title="Cursiva (⌘I)" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="size-3.5" />
      </TbBtn>
      <TbBtn title="Tachado" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="size-3.5" />
      </TbBtn>
      <Sep />
      {!compact && (
        <TbBtn title="Título H1" active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <Heading1 className="size-3.5" />
        </TbBtn>
      )}
      <TbBtn title="Título H2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <Heading2 className="size-3.5" />
      </TbBtn>
      <TbBtn title="Título H3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        <Heading3 className="size-3.5" />
      </TbBtn>
      <Sep />
      <TbBtn title="Lista" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="size-3.5" />
      </TbBtn>
      <TbBtn title="Lista numerada" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="size-3.5" />
      </TbBtn>
      <TbBtn title="Cita" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Quote className="size-3.5" />
      </TbBtn>
      <TbBtn title="Código en línea" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>
        <Code className="size-3.5" />
      </TbBtn>
      <TbBtn title="Bloque de código" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
        <Code2 className="size-3.5" />
      </TbBtn>
      {!compact && (
        <TbBtn title="Línea horizontal" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus className="size-3.5" />
        </TbBtn>
      )}
      <Sep />
      <TbBtn title="Enlace" active={editor.isActive('link')} onClick={onLink}>
        <LinkIcon className="size-3.5" />
      </TbBtn>
      <TbBtn title="Insertar imagen" onClick={onImage}>
        <ImageIcon className="size-3.5" />
      </TbBtn>
      {!compact && (
        <>
          <Sep />
          <TbBtn title="Alinear izq." active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
            <AlignLeft className="size-3.5" />
          </TbBtn>
          <TbBtn title="Centrar" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
            <AlignCenter className="size-3.5" />
          </TbBtn>
          <TbBtn title="Alinear der." active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
            <AlignRight className="size-3.5" />
          </TbBtn>
        </>
      )}
      <Sep />
      <TbBtn title="Quitar formato" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
        <RemoveFormatting className="size-3.5" />
      </TbBtn>
      <Sep />
      <TbBtn title="Deshacer (⌘Z)" onClick={() => editor.chain().focus().undo().run()}>
        <Undo className="size-3.5" />
      </TbBtn>
      <TbBtn title="Rehacer (⌘⇧Z)" onClick={() => editor.chain().focus().redo().run()}>
        <Redo className="size-3.5" />
      </TbBtn>
      {aiButton && (
        <>
          <span className="ml-auto" />
          {aiButton}
        </>
      )}
    </div>
  );
}

function Sep() {
  return <span className="mx-1 my-1 w-px self-stretch bg-neutral-200" />;
}

function TbBtn({
  active, onClick, children, title,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded text-neutral-600 transition-colors',
        active ? 'bg-neutral-900 text-white' : 'hover:bg-neutral-100 hover:text-neutral-900',
      )}
    >
      {children}
    </button>
  );
}
